import React, { useCallback, useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ChatIcon from '@mui/icons-material/Chat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { PrivateContent } from '../../services/api';
import { getS3Url } from '../../utils/imageUtils';
import { getLatestContentMetrics } from '../../utils/contentMetricsUtils';
import { formatChileanDateLong } from '../../utils/dateUtils';
import ProgressiveImage from '../common/ProgressiveImage';

// Format numbers in compact notation (K, M)
const formatCompactNumber = (num: number): string => {
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toString();
};

const VERTICAL_IMAGE_ASPECT_RATIO = 9 / 16;

export const PrivateContentCard: React.FC<{
    item: PrivateContent;
    onClick?: (item: PrivateContent) => void;
    /** Use vertical (9:16) aspect ratio for the image, e.g. for Shorts/Reels/TikTok */
    imageAspectRatio?: 'standard' | 'vertical';
    /** When false, hide livestream indicator (company podcast setting). Default false. */
    showLivestreamData?: boolean;
}> = ({ item, onClick, imageAspectRatio = 'standard', showLivestreamData = false }) => {
    const handleClick = useCallback(() => {
        if (onClick) {
            onClick(item);
        } else if (item.url) {
            window.open(item.url, '_blank');
        }
    }, [item, onClick]);

    const imageUrl = useMemo(() => {
        // Prioritize imageS3Key, then fall back to thumbnail or imageUrl
        if (item.imageS3Key) {
            return getS3Url(item.imageS3Key);
        }
        return item.thumbnail || item.imageUrl;
    }, [item]);

    const stats = useMemo(() => {
        // Use latest dailyMetrics when available (daily-updated), else insights/root fields
        const m = getLatestContentMetrics(item, item.source);
        return {
            views: m.views,
            likes: m.likes,
            comments: m.comments,
            shares: m.shares,
            saves: m.saves,
            reach: m.reach,
        };
    }, [item]);

    const formatDate = useMemo(() => {
        return formatChileanDateLong(item.publishedAt) || null;
    }, [item.publishedAt]);

    // Define priority order and available metrics
    const availableMetrics = useMemo(() => {
        const metrics = [
            {
                key: 'reach',
                value: stats.reach,
                icon: <TrendingUpIcon sx={{ fontSize: 14 }} />,
                label: 'Alcance',
                priority: 1
            },
            {
                key: 'views',
                value: stats.views,
                icon: <VisibilityIcon sx={{ fontSize: 14 }} />,
                label: 'Visualizaciones',
                priority: 2
            },
            {
                key: 'likes',
                value: stats.likes,
                icon: <ThumbUpIcon sx={{ fontSize: 14 }} />,
                label: 'Me gusta',
                priority: 3
            },
            {
                key: 'comments',
                value: stats.comments,
                icon: <ChatIcon sx={{ fontSize: 14 }} />,
                label: 'Comentarios',
                priority: 4
            },
            {
                key: 'shares',
                value: stats.shares,
                icon: <ShareIcon sx={{ fontSize: 14 }} />,
                label: 'Compartidos',
                priority: 5
            },
            {
                key: 'saves',
                value: stats.saves,
                icon: <BookmarkIcon sx={{ fontSize: 14 }} />,
                label: 'Guardados',
                priority: 6
            },
        ];

        return metrics
            .filter(m => m.value > 0)
            .sort((a, b) => a.priority - b.priority);
    }, [stats]);

    // Vertical layout (Shorts/Reels/TikTok): show only views; rest in "+n más" pill
    const topMetrics = useMemo(() => {
        if (imageAspectRatio === 'vertical') {
            const views = availableMetrics.find(m => m.key === 'views');
            return views ? [views] : [];
        }
        return availableMetrics.slice(0, 3);
    }, [availableMetrics, imageAspectRatio]);
    const remainingMetrics = useMemo(() => {
        if (imageAspectRatio === 'vertical') {
            return availableMetrics.filter(m => m.key !== 'views');
        }
        return availableMetrics.slice(3);
    }, [availableMetrics, imageAspectRatio]);
    const remainingCount = remainingMetrics.length;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                cursor: 'pointer',
                height: '100%',
            }}
            onClick={handleClick}
        >
            {/* Top - Image */}
            {imageUrl && (
                <Box
                    sx={{
                        borderRadius: 1,
                        position: 'relative',
                        ...(imageAspectRatio === 'vertical'
                            ? {
                                  width: '100%',
                                  aspectRatio: VERTICAL_IMAGE_ASPECT_RATIO,
                                  overflow: 'hidden',
                              }
                            : {}
                        ),
                    }}
                >
                    {showLivestreamData && item.livestreamId && (
                        <Tooltip title="Tiene stream asociado">
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 28,
                                    height: 28,
                                    borderRadius: 1,
                                    backgroundColor: '#fff',
                                    color: '#f44336',
                                }}
                            >
                                <FiberManualRecordIcon sx={{ fontSize: 10 }} />
                            </Box>
                        </Tooltip>
                    )}
                    <ProgressiveImage
                        src={imageUrl}
                        alt={item.title}
                        sx={{
                            ...(imageAspectRatio === 'vertical'
                                ? {
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                  }
                                : {
                                      height: 160,
                                      objectFit: 'cover',
                                      width: '100%',
                                      borderRadius: 1,
                                  }
                            ),
                        }}
                    />
                </Box>
            )}

            {/* Bottom - Content */}
            <Box
                sx={{
                    pt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ mb: 1 }}>
                    <Typography
                        variant="h3"
                        sx={{
                            textAlign: 'left',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            fontSize: '1rem',
                            color: 'primary.main',
                        }}
                    >
                        {item.title}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Top 3 Metrics */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {topMetrics.map((metric) => (
                            <Tooltip key={metric.key} title={`${metric.label}: ${metric.value.toLocaleString()}`}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}
                                >
                                    {metric.icon}
                                    {formatCompactNumber(metric.value)}
                                </Typography>
                            </Tooltip>
                        ))}
                        {remainingCount > 0 && (
                            <Tooltip
                                title={
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {remainingMetrics.map((metric) => (
                                            <Box
                                                key={metric.key}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                {metric.icon}
                                                <span>{metric.label}: {metric.value.toLocaleString()}</span>
                                            </Box>
                                        ))}
                                    </Box>
                                }
                                >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 3,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        fontSize: '0.7rem',
                                        color: 'text.secondary',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            backgroundColor: 'action.hover',
                                        }
                                    }}
                                >
                                    +{remainingCount} más
                                </Box>
                            </Tooltip>
                        )}
                    </Box>

                    {/* Date Row */}
                    {formatDate && (
                        <Tooltip title="Fecha de publicación">
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}
                            >
                                {formatDate}
                            </Typography>
                        </Tooltip>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
